/**
 * Loan field constants, section definitions, cross-population logic,
 * and formatting utilities shared across AI Application Flow and Review components.
 */

export const LOAN_TYPE_OPTIONS = [
  { id: 'sba_loan', label: 'SBA Loan', desc: '$25K - $5M', color: 'bg-blue-50 border-blue-200 text-blue-800' },
  { id: 'term_loan_10yr', label: '10 Year Term Loan', desc: 'Up to $200K', color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
  { id: 'term_loan_3_5yr', label: 'EZ Term Loan 3-5 Yr', desc: '$25K - $500K', color: 'bg-teal-50 border-teal-200 text-teal-800' },
  { id: 'term_loc_hybrid', label: 'Term Loan / LOC Hybrid', desc: '$50K – $100K', color: 'bg-cyan-50 border-cyan-200 text-cyan-800' },
  { id: 'line_of_credit', label: 'Line of Credit', desc: '$10K - $250K', color: 'bg-amber-50 border-amber-200 text-amber-800' },
  { id: 'equipment_financing', label: 'Equipment Financing', desc: 'New & used equipment', color: 'bg-purple-50 border-purple-200 text-purple-800' },
  { id: 'merchant_processing', label: 'Merchant Processing', desc: 'Payment processing services', color: 'bg-rose-50 border-rose-200 text-rose-800' },
  { id: 'jumbo_term_loan', label: 'Jumbo Term Loan / LOC', desc: '$150K – $25M', color: 'bg-slate-50 border-slate-300 text-slate-800' },
  { id: 'transportation_factoring', label: 'Transportation Factoring', desc: 'Freight factoring', color: 'bg-orange-50 border-orange-200 text-orange-800' },
  { id: 'real_estate_financing', label: 'Real Estate Financing', desc: 'Commercial real estate', color: 'bg-stone-50 border-stone-300 text-stone-800' },
  { id: 'debt_consulting', label: 'Debt Consolidation', desc: 'Refi or restructure existing debt', color: 'bg-indigo-50 border-indigo-200 text-indigo-800' },
  { id: 'foreclosure_bailout', label: 'Foreclosure Bailout', desc: 'Rescue financing before auction', color: 'bg-red-50 border-red-200 text-red-800' },
  { id: 'family_office', label: 'Family Office Programs', desc: 'Multi-program family office funding', color: 'bg-violet-50 border-violet-200 text-violet-800' },
];

// Central label resolver — always use this instead of raw
// `product_type.replace(/_/g, ' ')` so users see "Debt Consolidation" instead
// of "debt consulting".
export const productLabel = (productType) => {
  const found = LOAN_TYPE_OPTIONS.find(o => o.id === productType);
  if (found) return found.label;
  // Fallback for any legacy/unknown product_type
  return String(productType || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

// Family Office subtypes (multi-select). Debt Consolidation is intentionally
// excluded — that's its own product type (`debt_consulting`).
// Foreclosure Bailout display name: Business Name → vesting entity → intake business name → borrower.
export const getForeclosureName = (app) => {
  const fb = app?.foreclosure_bailout_data;
  if (!fb) return null;
  const borrower = `${fb.borrower_first_name || ''} ${fb.borrower_last_name || ''}`.trim();
  return fb.business_name || fb.entity_name || app.intake_contact?.business_name || borrower || null;
};

export const FAMILY_OFFICE_SUBTYPES = [
  { id: 'term_loans',                label: 'Term Loans',              desc: 'Fixed-rate financing with predictable monthly payments' },
  { id: 'lines_of_credit',           label: 'Lines of Credit',         desc: 'Flexible revolving capital — pay interest on what you use' },
  { id: 'non_collateralized_loans',  label: 'Non-Collateralized Loans', desc: 'Unsecured financing based on business performance' },
  { id: 'asset_based_lending',       label: 'Asset-Based Lending',     desc: 'Loan secured by AR, inventory, equipment, or real estate' },
  { id: 'equipment_financing',       label: 'Equipment Financing',     desc: 'Acquire or lease equipment while preserving cash flow' },
];

export const DEBT_SCHEDULE_TYPES = ['sba_loan', 'term_loan_10yr', 'term_loan_3_5yr', 'term_loc_hybrid', 'jumbo_term_loan', 'debt_consulting'];

const TERM_LOAN_SECTIONS = [
  { title: 'Primary Owner', fields: [
    { key: 'first_name', label: 'First Name' }, { key: 'last_name', label: 'Last Name' },
    { key: 'ownership_percentage', label: 'Ownership %' }, { key: 'date_of_birth', label: 'DOB' },
    { key: 'email_address', label: 'Email' }, { key: 'phone_number', label: 'Phone' },
    { key: 'social_security_number', label: 'SSN' }, { key: 'home_address', label: 'Address' },
  ]},
  { title: 'Business', fields: [
    { key: 'legal_corporate_name', label: 'Corporate Name' }, { key: 'doing_business_as', label: 'DBA' },
    { key: 'business_address', label: 'Address' }, { key: 'business_city', label: 'City' },
    { key: 'business_state', label: 'State' }, { key: 'business_zip', label: 'ZIP' },
    { key: 'entity_type', label: 'Entity Type' }, { key: 'federal_tax_id', label: 'Tax ID' },
    { key: 'industry_type', label: 'Industry' }, { key: 'business_start_date', label: 'Business Start Date' },
    { key: 'state_of_incorporation', label: 'State of Incorporation' }, { key: 'number_of_employees', label: 'Employees' },
  ]},
  { title: 'Financial', fields: [
    { key: 'annual_revenue', label: 'Annual Revenue', fmt: 'currency' },
    { key: 'monthly_revenue', label: 'Monthly Revenue', fmt: 'currency' },
    { key: 'funding_amount_requested', label: 'Funding Requested', fmt: 'currency' },
    { key: 'use_of_funds', label: 'Use of Funds' },
  ]},
];

const GROWTH_TERM_SECTIONS = [
  { title: 'Loan Purpose', fields: [
    { key: 'use_of_proceeds', label: 'Use(s) of Proceeds' },
    { key: 'use_of_proceeds_amounts', label: 'Amount per Use' },
    { key: 'total_loan_amount', label: 'Total Loan Amount', fmt: 'currency' },
  ]},
  { title: 'Business Details', fields: [
    { key: 'business_name', label: 'Business Name' }, { key: 'doing_business_as', label: 'DBA / Trade Name' },
    { key: 'business_structure', label: 'Business Structure' },
    { key: 'average_annual_revenue', label: 'Avg Annual Revenue', fmt: 'currency' },
    { key: 'date_established', label: 'Date Established' }, { key: 'federal_tax_id', label: 'Tax ID' },
    { key: 'business_phone', label: 'Business Phone' }, { key: 'company_website', label: 'Company Website' },
    { key: 'naics_code', label: 'Industry / NAICS Code' },
  ]},
  { title: 'Business Address', fields: [
    { key: 'business_address', label: 'Address' }, { key: 'business_city', label: 'City' },
    { key: 'business_state', label: 'State' }, { key: 'business_zip', label: 'ZIP' },
  ]},
  { title: 'Relationship to Business', fields: [
    { key: 'role_at_business', label: 'Role' }, { key: 'title_at_company', label: 'Title' },
    { key: 'ownership_percentage', label: 'Ownership %' }, { key: 'years_with_business', label: 'Years with Business' },
    { key: 'years_industry_experience', label: 'Years Industry Experience' },
  ]},
  { title: 'Personal Details', fields: [
    { key: 'first_name', label: 'First Name' }, { key: 'last_name', label: 'Last Name' },
    { key: 'email_address', label: 'Email' }, { key: 'phone_number', label: 'Phone' },
    { key: 'ssn_itin', label: 'SSN / ITIN' }, { key: 'date_of_birth', label: 'Date of Birth' },
    { key: 'place_of_birth', label: 'Place of Birth' },
  ]},
  { title: 'Identification', fields: [
    { key: 'id_type', label: 'ID Type' }, { key: 'id_number', label: 'ID Number' },
    { key: 'id_state_issued', label: 'State Issued' }, { key: 'id_expiration_date', label: 'Expiration Date' },
  ]},
  { title: 'Home Address', fields: [
    { key: 'owns_residence', label: 'Own Residence?' }, { key: 'home_address', label: 'Address' },
    { key: 'home_city', label: 'City' }, { key: 'home_state', label: 'State' }, { key: 'home_zip', label: 'ZIP' },
  ]},
  { title: 'Other', fields: [
    { key: 'citizenship_status', label: 'Citizenship Status' },
    { key: 'other_business_interests', label: 'Other Business Interests' },
  ]},
];

const EZ_TERM_35_SECTIONS = [
  { title: 'Primary Borrower Contact Information', fields: [
    { key: 'first_name', label: 'First Name' },
    { key: 'last_name', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'send_welcome_email', label: 'Send Welcome Email?', fmt: 'bool' },
    { key: 'phone', label: 'Phone' },
    { key: 'primary_contact_ssn', label: 'Primary Contact SSN' },
  ]},
  { title: 'Primary Contact Address', fields: [
    { key: 'primary_street_address', label: 'Street Address' },
    { key: 'primary_street_address_2', label: 'Street Address 2' },
    { key: 'primary_city', label: 'City' },
    { key: 'primary_state', label: 'State' },
    { key: 'primary_zip', label: 'Zip' },
    { key: 'percentage_ownership', label: 'Percentage Ownership in Business' },
    { key: 'guaranty_type', label: 'Guaranty Type' },
  ]},
  { title: 'Business Information', fields: [
    { key: 'legal_corporate_name', label: 'Legal/Corporate Name' },
    { key: 'company_name_dba', label: 'Company Name (DBA)' },
    { key: 'industry', label: 'Industry' },
    { key: 'office_space', label: 'Office Space' },
    { key: 'business_type', label: 'Business Type' },
    { key: 'business_location_type', label: 'Business Location Type' },
    { key: 'state_of_incorporation', label: 'State of Incorporation' },
    { key: 'business_start_date', label: 'Business Start Date' },
    { key: 'type_of_business', label: 'Type of Business' },
    { key: 'website', label: 'Website' },
  ]},
  { title: 'Physical Address', fields: [
    { key: 'physical_street_address', label: 'Street Address' },
    { key: 'physical_street_address_2', label: 'Street Address 2' },
    { key: 'physical_city', label: 'City' },
    { key: 'physical_state', label: 'State' },
    { key: 'physical_zip', label: 'Zip' },
  ]},
  { title: 'Mailing Address', fields: [
    { key: 'mailing_same_as_physical', label: 'Same as Physical Address', fmt: 'bool' },
    { key: 'mailing_street_address', label: 'Street Address' },
    { key: 'mailing_street_address_2', label: 'Street Address 2' },
    { key: 'mailing_city', label: 'City' },
    { key: 'mailing_state', label: 'State' },
    { key: 'mailing_zip', label: 'Zip' },
  ]},
  { title: 'Financial Information', fields: [
    { key: 'gross_annual_sales', label: 'Gross Annual Sales', fmt: 'currency' },
    { key: 'funding_amount_requested', label: 'Funding Amount Requested', fmt: 'currency' },
    { key: 'use_of_funds_description', label: 'Use of Funds Description' },
  ]},
];

// Enum-style option lists for the EZ Term Loan 3-5 Yr dropdowns. Consumed by
// dynamic form renderers (BorrowerCompletionFlow, IntakePublicForm, etc.) that
// key off `FIELD_OPTIONS[product_type][field_key]`.
export const EZ_TERM_35_FIELD_OPTIONS = {
  guaranty_type: ['Full Unsecured', 'Full Secured', 'Limited Unsecured', 'Limited Secured'],
  office_space: ['Rent', 'Own'],
  business_type: [
    'Sole Proprietorship',
    'C Corporation',
    'S Corporation',
    'Limited Liability Partnership',
    'Partnership',
    'Limited Liability Company',
    'Other',
  ],
  business_location_type: ['Residential', 'Commercial'],
  type_of_business: ['Startup', 'Existing Business', 'Business Acquisition'],
};

export const FIELD_SECTIONS = {
  sba_loan: [
    { title: 'Loan Details', fields: [
      { key: 'loan_requested_amount', label: 'Amount', fmt: 'currency' },
      { key: 'has_existing_debt', label: 'Existing Debt', fmt: 'bool' },
      { key: 'refinance_existing_debt', label: 'Refinance', fmt: 'bool' },
      { key: 'total_monthly_sales', label: 'Monthly Sales', fmt: 'currency' },
    ]},
    { title: 'Business', fields: [
      { key: 'business_legal_name', label: 'Legal Name' }, { key: 'doing_business_as', label: 'DBA' },
      { key: 'legal_entity_type', label: 'Entity Type' }, { key: 'business_start_date', label: 'Start Date' },
      { key: 'federal_tax_id', label: 'Tax ID' }, { key: 'business_phone_number', label: 'Phone' },
      { key: 'business_address', label: 'Address' }, { key: 'naics_code_or_nature', label: 'NAICS/Industry' },
      { key: 'number_of_employees', label: 'Number of Employees' },
    ]},
    { title: 'Primary Applicant', fields: [
      { key: 'first_name', label: 'First Name' }, { key: 'last_name', label: 'Last Name' },
      { key: 'first_last_name', label: 'Name (legacy)' }, { key: 'ownership_percentage', label: 'Ownership %' },
      { key: 'date_of_birth', label: 'DOB' }, { key: 'social_security_number', label: 'SSN' },
      { key: 'mobile_phone_number', label: 'Phone' }, { key: 'email_address', label: 'Email' },
      { key: 'home_address', label: 'Address' },
    ]},
    { title: 'Financial', fields: [
      { key: 'annual_income', label: 'Annual Income', fmt: 'currency' },
      { key: 'assets_cash', label: 'Cash Assets', fmt: 'currency' }, { key: 'assets_home', label: 'Home Value', fmt: 'currency' },
      { key: 'assets_automobile', label: 'Auto Value', fmt: 'currency' },
      { key: 'assets_investment_accounts', label: 'Investments', fmt: 'currency' },
      { key: 'liabilities_mortgage', label: 'Mortgage', fmt: 'currency' },
      { key: 'liabilities_credit_cards', label: 'Credit Cards', fmt: 'currency' },
      { key: 'liabilities_auto_loans', label: 'Auto Loans', fmt: 'currency' },
    ]},
    { title: 'Business Financials', fields: [
      { key: 'fiscal_year', label: 'Fiscal Year' }, { key: 'revenue', label: 'Revenue', fmt: 'currency' },
      { key: 'cost_of_goods_sold', label: 'Cost of Goods Sold', fmt: 'currency' },
      { key: 'payroll', label: 'Payroll', fmt: 'currency' }, { key: 'rent', label: 'Rent', fmt: 'currency' },
      { key: 'other_operating_expenses', label: 'Other Operating Expenses', fmt: 'currency' },
    ]},
  ],
  term_loan_10yr: TERM_LOAN_SECTIONS,
  term_loan_3_5yr: EZ_TERM_35_SECTIONS,
  term_loc_hybrid: GROWTH_TERM_SECTIONS,
  term_loan: TERM_LOAN_SECTIONS,
  line_of_credit: [
    { title: 'Owner Information', fields: [
      { key: 'owner_first_name', label: 'First Name' },
      { key: 'owner_last_name', label: 'Last Name' },
      { key: 'owner_name', label: 'Owner Name (legacy)' },
      { key: 'owner_address', label: 'Owner Address' },
      { key: 'owner_ssn', label: 'SSN' },
      { key: 'owner_email', label: 'Email' },
      { key: 'owner_phone', label: 'Cell Phone' },
    ]},
    { title: 'Business Information', fields: [
      { key: 'business_name', label: 'Business Name' },
      { key: 'business_industry', label: 'Industry' },
      { key: 'years_in_business', label: 'Years in Business' },
      { key: 'business_zip_code', label: 'Business Zipcode' },
      { key: 'notes', label: 'Notes' },
      { key: 'business_website_type', label: 'Website / Social Media Type' },
      { key: 'business_website_link', label: 'Website / Social Media Link' },
    ]},
    { title: 'Credit Details', fields: [
      { key: 'requested_line_of_credit_amount', label: 'Credit Amount', fmt: 'currency' },
      { key: 'purpose_of_credit', label: 'Purpose' },
    ]},
  ],
  equipment_financing: [
    { title: 'Business', fields: [
      { key: 'business_name', label: 'Business Name' }, { key: 'business_address', label: 'Address' },
      { key: 'business_city', label: 'City' }, { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'ZIP' }, { key: 'entity_type', label: 'Entity Type' },
      { key: 'federal_tax_id', label: 'Tax ID' }, { key: 'years_in_business', label: 'Years in Business' },
      { key: 'industry', label: 'Industry' }, { key: 'annual_revenue', label: 'Annual Revenue', fmt: 'currency' },
    ]},
    { title: 'Owner', fields: [
      { key: 'owner_first_name', label: 'First Name' }, { key: 'owner_last_name', label: 'Last Name' },
      { key: 'owner_name', label: 'Name (legacy)' }, { key: 'owner_email', label: 'Email' },
      { key: 'owner_phone', label: 'Phone' }, { key: 'owner_ssn', label: 'SSN' },
      { key: 'owner_dob', label: 'DOB' }, { key: 'owner_address', label: 'Home Address' },
      { key: 'ownership_percentage', label: 'Ownership %' }, { key: 'fico_score', label: 'FICO Score' },
    ]},
    { title: 'Equipment', fields: [
      { key: 'equipment_type', label: 'Equipment Type' }, { key: 'equipment_description', label: 'Description' },
      { key: 'equipment_cost', label: 'Equipment Cost', fmt: 'currency' },
      { key: 'new_or_used', label: 'New or Used' }, { key: 'equipment_age_years', label: 'Age (Years)' },
      { key: 'vendor_name', label: 'Vendor Name' }, { key: 'vendor_contact', label: 'Vendor Contact' },
    ]},
    { title: 'Financing', fields: [
      { key: 'amount_requested', label: 'Amount Requested', fmt: 'currency' },
      { key: 'down_payment', label: 'Down Payment', fmt: 'currency' },
      { key: 'desired_term_months', label: 'Desired Term (Months)' },
      { key: 'use_of_equipment', label: 'Use of Equipment' },
      { key: 'has_existing_equipment_loans', label: 'Existing Equipment Loans', fmt: 'bool' },
    ]},
  ],
  merchant_processing: [
    { title: 'Contact Information', fields: [
      { key: 'contact_name', label: 'Contact Name' },
      { key: 'company_name', label: 'Company Name' },
      { key: 'industry_type', label: 'Industry Type' },
      { key: 'website_url', label: 'Website URL' },
      { key: 'years_in_business', label: 'Years in Business' },
      { key: 'monthly_volume', label: 'Monthly Volume', fmt: 'currency' },
    ]},
    { title: 'Point of Sale Details', fields: [
      { key: 'pos_software_or_terminals', label: 'POS Software or Terminals' },
      { key: 'pos_software_name', label: 'POS Software Name' },
      { key: 'number_of_terminals', label: 'Number of Terminals' },
      { key: 'terminal_brand_model', label: 'Terminal Brand and Model' },
    ]},
  ],
  jumbo_term_loan: [
    { title: 'Business Information', fields: [
      { key: 'business_legal_name', label: 'Business Legal Name' },
      { key: 'business_dba_name', label: 'Business DBA Name' },
      { key: 'type_of_business_entity', label: 'Type of Business Entity' },
      { key: 'industry_type', label: 'Industry Type' },
      { key: 'state_of_incorporation', label: 'State of Incorporation' },
      { key: 'business_street_address', label: 'Business Street Address' },
      { key: 'business_apt_suite', label: 'Apartment, Suite, Room' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'ZIP' },
      { key: 'amount_requested', label: 'Amount Requested', fmt: 'currency' },
      { key: 'business_federal_tax_id', label: 'Business Federal Tax ID #' },
      { key: 'business_start_date', label: 'Business Start Date (MM/DD/YYYY)' },
      { key: 'website', label: 'Website' },
    ]},
    { title: 'Owner Information', fields: [
      { key: 'owner_first_name', label: 'Legal First Name' },
      { key: 'owner_last_name', label: 'Legal Last Name' },
      { key: 'owner_dob', label: 'Date of Birth (MM/DD/YYYY)' },
      { key: 'owner_street_address', label: 'Home Street Address' },
      { key: 'owner_apt_suite', label: 'Apartment, Suite, Room' },
      { key: 'owner_city', label: 'City' },
      { key: 'owner_state', label: 'State' },
      { key: 'owner_zip', label: 'ZIP' },
      { key: 'owner_ssn', label: 'Social Security #' },
    ]},
  ],
  transportation_factoring: [
    { title: 'Applicant Information', fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'mc_number', label: 'MC Number' },
      { key: 'email', label: 'Email' },
      { key: 'phone_number', label: 'Phone Number' },
    ]},
  ],
  real_estate_financing: [
    { title: 'Request for Financing', fields: [
      { key: 'use_of_financing', label: 'Use of Financing (Select all that apply)', fmt: 'multiselect',
        options: ['Working Capital', 'Other', 'Debt Consolidation'] },
      { key: 'financing_amount_requested', label: 'Financing Amount Requested', fmt: 'currency' },
      { key: 'down_payment_amount', label: 'Down Payment Amount', fmt: 'currency' },
      { key: 'detailed_use_of_funds', label: 'Detailed Use of Funds', fmt: 'textarea' },
    ]},
    { title: 'Business Information', fields: [
      { key: 'legal_name_of_business', label: 'Legal Name of Business' },
      { key: 'business_in_good_standing', label: 'Is Business in Good Standing / Active?', fmt: 'bool' },
      { key: 'date_established', label: 'Date Established' },
      { key: 'tax_id', label: 'Tax ID #' },
      { key: 'business_type', label: 'Business Type', fmt: 'select',
        options: ['Corp', 'LLC', 'Sole-Proprietor'] },
    ]},
    { title: 'Address', fields: [
      { key: 'business_address', label: 'Address of Business Location' },
      { key: 'business_city_state_zip', label: 'City, State, Zip' },
      { key: 'mailing_address', label: 'Mailing Address (if different)' },
      { key: 'mailing_city_state_zip', label: 'City, State, Zip' },
    ]},
    { title: 'Contact', fields: [
      { key: 'main_phone', label: 'Main Phone' },
      { key: 'fax', label: 'Fax' },
      { key: 'number_of_employees', label: '# of Employees' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'type_of_business', label: 'Type of Business' },
    ]},
    // ---- Pre-Qualification Intake (additional, all optional) ----
    { title: 'Borrower Information', fields: [
      { key: 'borrower_first_name', label: 'First Name' },
      { key: 'borrower_last_name', label: 'Last Name' },
      { key: 'borrower_address', label: 'Street Address' },
      { key: 'borrower_city', label: 'City' },
      { key: 'borrower_state', label: 'State' },
      { key: 'borrower_zip', label: 'Zip' },
      { key: 'borrower_date_of_birth', label: 'Date of Birth' },
      { key: 'borrower_ssn', label: 'Social Security Number' },
      { key: 'borrower_email', label: 'Email' },
      { key: 'borrower_phone', label: 'Contact Number' },
      { key: 'borrower_citizenship_status', label: 'Citizenship Status', fmt: 'select',
        options: ['US Citizen', 'Legal Permanent Resident', 'Foreign National'] },
    ]},
    { title: 'Property Information', fields: [
      { key: 'property_address', label: 'Property Address' },
      { key: 'property_type', label: 'Property Type' },
      { key: 'number_of_units', label: 'Number of Units' },
      { key: 'total_sqft', label: 'Total SQFT' },
      { key: 'purchase_price', label: 'Purchase Price', fmt: 'currency' },
      { key: 'property_under_contract', label: 'Property Under Contract?', fmt: 'select',
        options: ['Yes', 'No'] },
      { key: 'original_purchase_price', label: 'Original Purchase Price (if already owned)', fmt: 'currency' },
      { key: 'original_purchase_date', label: 'Original Purchase Date (if already owned)' },
      { key: 'existing_payoff_amount', label: 'Existing Payoff Amount (if already owned)', fmt: 'currency' },
      { key: 'as_is_value', label: 'As-Is Value', fmt: 'currency' },
      { key: 'rehab_budget', label: 'Rehab Budget', fmt: 'currency' },
      { key: 'estimated_after_repair_value', label: 'Estimated After Repair Value (ARV)', fmt: 'currency' },
    ]},
    { title: 'Financial Information', fields: [
      { key: 'liquidity_cash_to_close', label: 'Liquidity / Cash to Close (if applicable)', fmt: 'currency' },
      { key: 'exit_strategy', label: 'Exit Strategy', fmt: 'select',
        options: ['Sell', 'Refinance'] },
      { key: 'monthly_rental_income', label: 'Monthly Rental Income (if exit is Refinance)', fmt: 'currency' },
      { key: 'annual_taxes', label: 'Annual Taxes', fmt: 'currency' },
      { key: 'annual_insurance', label: 'Annual Insurance', fmt: 'currency' },
      { key: 'estimated_middle_fico', label: 'Estimated Middle FICO Score' },
    ]},
    { title: 'Experience', fields: [
      { key: 'completed_rehab_projects_3yr', label: 'Completed Rehab Projects in the Past 3 Years' },
    ]},
    { title: 'Additional Information', fields: [
      { key: 'additional_notes', label: 'Additional Notes regarding the loan/borrower', fmt: 'textarea' },
    ]},
  ],
  debt_consulting: [
    { title: 'Business Information', fields: [
      { key: 'legal_business_name', label: 'Legal Business Name' },
      { key: 'dba', label: 'Doing Business As (DBA)' },
      { key: 'business_phone', label: 'Business Phone' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'business_street_address', label: 'Business Street Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'Zip' },
      { key: 'business_start_date', label: 'Business Start Date' },
      { key: 'business_type_services', label: 'Business Type / Services Provided', fmt: 'textarea' },
      { key: 'legal_structure', label: 'Legal Structure', fmt: 'select',
        options: ['Sole Proprietorship', 'C Corporation', 'S Corporation', 'Limited Liability Partnership', 'Partnership', 'Limited Liability Company', 'Other'] },
      { key: 'landlord_name', label: 'Landlord Name' },
      { key: 'landlord_phone', label: 'Landlord Phone' },
    ]},
    { title: 'Financial Information', fields: [
      { key: 'federal_tax_id', label: 'Federal Tax ID / EIN (9 digits)' },
      { key: 'state_of_incorporation', label: 'State of Incorporation' },
      { key: 'requested_funding_amount', label: 'Requested Funding Amount', fmt: 'currency' },
      { key: 'purpose_of_funds', label: 'Purpose of Funds', fmt: 'textarea' },
    ]},
    { title: 'Advances & Debts', fields: [
      { key: 'has_merchant_advances', label: 'Do you currently have any merchant cash advances or business loans outstanding?', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'has_other_business_debts', label: 'Do you have any other outstanding business debts?', fmt: 'select', options: ['Yes', 'No'] },
    ]},
    { title: 'Outstanding Debts / Advances', fmt: 'repeater', repeaterKey: 'debts', addButtonLabel: 'Add Another Debt', minRows: 0, fields: [
      { key: 'funding_company', label: 'Funding Company' },
      { key: 'amount_funded', label: 'Amount Funded', fmt: 'currency' },
      { key: 'date_funded', label: 'Date Funded' },
      { key: 'payment_amount', label: 'Daily/Weekly Payment', fmt: 'currency' },
      { key: 'current_balance', label: 'Current Balance', fmt: 'currency' },
      { key: 'weeks_remaining', label: 'Payback Weeks Remaining' },
    ]},
    { title: 'Ownership — Primary Owner', fields: [
      { key: 'owner1_first_name', label: 'First Name' },
      { key: 'owner1_last_name', label: 'Last Name' },
      { key: 'owner1_email', label: 'Email' },
      { key: 'owner1_phone', label: 'Phone' },
      { key: 'owner1_date_of_birth', label: 'Date of Birth' },
      { key: 'owner1_ownership_percent', label: 'Ownership %' },
      { key: 'owner1_home_street_address', label: 'Home Street Address' },
      { key: 'owner1_home_city', label: 'City' },
      { key: 'owner1_home_state', label: 'State' },
      { key: 'owner1_home_zip', label: 'Zip' },
      { key: 'has_co_owner', label: 'Is there a co-owner who should also be on this application?', fmt: 'select', options: ['Yes', 'No'] },
    ]},
  ],
  foreclosure_bailout: [
    { title: 'Property Type', fields: [
      { key: 'property_type', label: 'Property Type' },
    ]},
    { title: 'Property Location', fields: [
      { key: 'property_state', label: 'State' },
    ]},
    { title: 'Property / Loan Information', fields: [
      { key: 'estimated_property_value', label: 'Estimated Property Value', fmt: 'currency' },
      { key: 'total_mortgage_balances', label: 'Total Amount of Mortgage Balances', fmt: 'currency' },
      { key: 'loan_amount', label: 'Loan Amount', fmt: 'currency' },
    ]},
    { title: 'Foreclosure Information', fields: [
      { key: 'foreclosure_sale_scheduled', label: 'Is a foreclosure sale or auction date scheduled?' },
      { key: 'foreclosure_sale_date', label: 'Foreclosure sale / auction date' },
    ]},
    { title: 'Borrower Information', fields: [
      { key: 'business_name', label: 'Business Name' },
      { key: 'borrower_first_name', label: 'Borrower First Name' },
      { key: 'borrower_last_name', label: 'Borrower Last Name' },
      { key: 'borrower_email', label: 'Borrower Email' },
      { key: 'borrower_phone', label: 'Borrower Phone' },
    ]},
    { title: 'Vesting', fields: [
      { key: 'title_held_in_entity', label: 'Is the title held in an entity?', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'entity_name', label: 'Entity Name', showIf: (d) => d?.title_held_in_entity === 'Yes' },
      { key: 'entity_ein', label: 'Entity EIN', showIf: (d) => d?.title_held_in_entity === 'Yes' },
    ]},
    { title: 'Loan Structure', fields: [
      { key: 'payment_structure', label: 'Payment Structure' },
      { key: 'broker_fee_percent', label: 'Broker Fee (%)' },
    ]},
  ],
  family_office: [
    { title: 'Sub-Programs (choose one or more)', fields: [
      { key: 'selected_subtypes', label: 'Programs of Interest', fmt: 'multiselect',
        options: ['Term Loans', 'Lines of Credit', 'Non-Collateralized Loans', 'Asset-Based Lending', 'Equipment Financing'],
        optionValues: ['term_loans', 'lines_of_credit', 'non_collateralized_loans', 'asset_based_lending', 'equipment_financing'] },
    ]},
    { title: 'Business Information', fields: [
      { key: 'legal_business_name', label: 'Legal Business Name' },
      { key: 'dba', label: 'Doing Business As (DBA)' },
      { key: 'business_phone', label: 'Business Phone' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'business_street_address', label: 'Business Street Address' },
      { key: 'business_city', label: 'City' },
      { key: 'business_state', label: 'State' },
      { key: 'business_zip', label: 'Zip' },
      { key: 'business_start_date', label: 'Business Start Date' },
      { key: 'business_type_services', label: 'Business Type / Services Provided', fmt: 'textarea' },
      { key: 'legal_structure', label: 'Legal Structure', fmt: 'select',
        options: ['Sole Proprietorship', 'Partnership', 'Corporation', 'LLC', 'Other'] },
      { key: 'landlord_name', label: 'Landlord Name' },
      { key: 'landlord_phone', label: 'Landlord Phone' },
    ]},
    { title: 'Financial Information', fields: [
      { key: 'federal_tax_id', label: 'Federal Tax ID / EIN (9 digits)' },
      { key: 'state_of_incorporation', label: 'State of Incorporation' },
      { key: 'requested_funding_amount', label: 'Requested Funding Amount', fmt: 'currency' },
      { key: 'purpose_of_funds', label: 'Purpose of Funds', fmt: 'textarea' },
    ]},
    { title: 'Advances & Debts', fields: [
      { key: 'has_merchant_advances', label: 'Do you currently have any merchant cash advances or business loans outstanding?', fmt: 'select', options: ['Yes', 'No'] },
      { key: 'has_other_business_debts', label: 'Do you have any other outstanding business debts?', fmt: 'select', options: ['Yes', 'No'] },
    ]},
    { title: 'Outstanding Debts / Advances', fmt: 'repeater', repeaterKey: 'debts', addButtonLabel: 'Add Another Debt', minRows: 0, fields: [
      { key: 'funding_company', label: 'Funding Company' },
      { key: 'amount_funded', label: 'Amount Funded', fmt: 'currency' },
      { key: 'date_funded', label: 'Date Funded' },
      { key: 'payment_amount', label: 'Daily/Weekly Payment', fmt: 'currency' },
      { key: 'current_balance', label: 'Current Balance', fmt: 'currency' },
      { key: 'weeks_remaining', label: 'Payback Weeks Remaining' },
    ]},
    { title: 'Ownership — Primary Owner', fields: [
      { key: 'owner1_first_name', label: 'First Name' },
      { key: 'owner1_last_name', label: 'Last Name' },
      { key: 'owner1_email', label: 'Email' },
      { key: 'owner1_phone', label: 'Phone' },
      { key: 'owner1_date_of_birth', label: 'Date of Birth' },
      { key: 'owner1_ssn', label: 'Social Security Number' },
      { key: 'owner1_ownership_percent', label: 'Ownership %' },
      { key: 'owner1_home_street_address', label: 'Home Street Address' },
      { key: 'owner1_home_city', label: 'City' },
      { key: 'owner1_home_state', label: 'State' },
      { key: 'owner1_home_zip', label: 'Zip' },
    ]},
    { title: 'Additional Owners', fmt: 'repeater', repeaterKey: 'additional_owners', addButtonLabel: 'Add Another Owner', minRows: 0, fields: [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'ssn', label: 'Social Security Number' },
      { key: 'ownership_percent', label: 'Ownership %' },
      { key: 'home_street_address', label: 'Home Street Address' },
      { key: 'home_city', label: 'City' },
      { key: 'home_state', label: 'State' },
      { key: 'home_zip', label: 'Zip' },
    ]},
  ],
};

export const formatValue = (value, fmt) => {
  if (value === null || value === undefined || value === '') return null;
  if (fmt === 'currency') return `$${Number(value).toLocaleString()}`;
  if (fmt === 'bool') return value === true || value === 'true' ? 'Yes' : value === false || value === 'false' ? 'No' : String(value);
  return String(value);
};

export const REQUIRED_FIELDS = {
  sba_loan: ['loan_requested_amount', 'has_existing_debt', 'total_monthly_sales', 'business_legal_name', 'legal_entity_type', 'business_start_date', 'federal_tax_id', 'business_phone_number', 'business_address', 'naics_code_or_nature', 'first_name', 'last_name', 'ownership_percentage', 'date_of_birth', 'social_security_number', 'mobile_phone_number', 'email_address', 'home_address'],
  term_loan_10yr: ['first_name', 'last_name', 'ownership_percentage', 'email_address', 'phone_number', 'legal_corporate_name', 'business_address', 'funding_amount_requested'],
  term_loc_hybrid: ['first_name', 'last_name', 'email_address', 'business_name', 'business_structure', 'federal_tax_id', 'business_address', 'total_loan_amount', 'use_of_proceeds', 'ownership_percentage', 'ssn_itin', 'date_of_birth'],
  term_loan_3_5yr: ['first_name', 'last_name', 'email', 'phone', 'legal_corporate_name', 'funding_amount_requested', 'use_of_funds_description'],
  term_loan: ['first_name', 'last_name', 'ownership_percentage', 'email_address', 'phone_number', 'legal_corporate_name', 'business_address', 'funding_amount_requested'],
  line_of_credit: ['business_name', 'requested_line_of_credit_amount', 'owner_first_name', 'owner_last_name', 'owner_email', 'owner_phone'],
  equipment_financing: ['business_name', 'owner_first_name', 'owner_last_name', 'owner_email', 'owner_phone', 'amount_requested'],
  merchant_processing: ['contact_name', 'company_name', 'industry_type'],
  jumbo_term_loan: ['business_legal_name', 'amount_requested', 'owner_first_name', 'owner_last_name'],
  transportation_factoring: ['first_name', 'last_name', 'email', 'phone_number'],
  real_estate_financing: ['legal_name_of_business'],
  debt_consulting: ['legal_business_name', 'business_email', 'business_phone', 'requested_funding_amount', 'purpose_of_funds', 'owner1_first_name', 'owner1_last_name', 'owner1_email'],
  foreclosure_bailout: ['property_type', 'property_state', 'estimated_property_value', 'total_mortgage_balances', 'foreclosure_sale_scheduled', 'borrower_first_name', 'borrower_last_name', 'borrower_email', 'borrower_phone', 'title_held_in_entity'],
  family_office: ['selected_subtypes', 'legal_business_name', 'business_email', 'business_phone', 'requested_funding_amount', 'purpose_of_funds', 'owner1_first_name', 'owner1_last_name', 'owner1_email'],
};

export const CROSS_POP_MAP = {
  business_name: {
    sba_loan_data: 'business_legal_name', term_loan_10yr_data: 'legal_corporate_name',
    term_loan_3_5yr_data: 'legal_corporate_name', term_loan_data: 'legal_corporate_name',
    term_loc_hybrid_data: 'legal_corporate_name',
    line_of_credit_data: 'business_name', equipment_financing_data: 'business_name',
    merchant_processing_data: 'company_name',
    jumbo_term_loan_data: 'business_legal_name',
    transportation_factoring_data: 'first_name',
    real_estate_financing_data: 'legal_name_of_business',
    foreclosure_bailout_data: 'business_name',
  },
  entity_type: {
    sba_loan_data: 'legal_entity_type', term_loan_10yr_data: 'entity_type',
    term_loan_3_5yr_data: 'business_type', term_loan_data: 'entity_type',
    term_loc_hybrid_data: 'entity_type',
    line_of_credit_data: 'business_type', equipment_financing_data: 'entity_type',
    real_estate_financing_data: 'business_type',
  },
  tax_id: {
    sba_loan_data: 'federal_tax_id', term_loan_10yr_data: 'federal_tax_id',
    term_loan_data: 'federal_tax_id',
    term_loc_hybrid_data: 'federal_tax_id',
    equipment_financing_data: 'federal_tax_id',
    real_estate_financing_data: 'tax_id',
  },
  business_address: {
    sba_loan_data: 'business_address', term_loan_10yr_data: 'business_address',
    term_loan_3_5yr_data: 'physical_street_address', term_loan_data: 'business_address',
    term_loc_hybrid_data: 'business_address',
    line_of_credit_data: 'business_address', equipment_financing_data: 'business_address',
    real_estate_financing_data: 'business_address',
  },
  loan_amount: {
    sba_loan_data: 'loan_requested_amount',
    term_loan_10yr_data: 'funding_amount_requested',
    term_loan_3_5yr_data: 'funding_amount_requested',
    term_loan_data: 'funding_amount_requested',
    term_loc_hybrid_data: 'total_loan_amount',
    line_of_credit_data: 'requested_line_of_credit_amount',
    equipment_financing_data: 'amount_requested',
    jumbo_term_loan_data: 'amount_requested',
    real_estate_financing_data: 'financing_amount_requested',
    foreclosure_bailout_data: 'loan_amount',
  },
  business_email: {
    real_estate_financing_data: 'business_email',
  },
  business_phone: {
    sba_loan_data: 'business_phone_number',
    real_estate_financing_data: 'main_phone',
  },
  date_established: {
    sba_loan_data: 'business_start_date', term_loan_10yr_data: 'business_start_date',
    term_loan_3_5yr_data: 'business_start_date', term_loan_data: 'business_start_date',
    term_loc_hybrid_data: 'business_start_date',
    jumbo_term_loan_data: 'business_start_date',
    real_estate_financing_data: 'date_established',
  },
  number_of_employees: {
    sba_loan_data: 'number_of_employees',
    term_loan_10yr_data: 'number_of_employees', term_loan_data: 'number_of_employees',
    real_estate_financing_data: 'number_of_employees',
  },
  owner_first_name: {
    sba_loan_data: 'first_name',
    term_loan_10yr_data: 'first_name',
    term_loan_3_5yr_data: 'first_name',
    term_loan_data: 'first_name',
    term_loc_hybrid_data: 'first_name',
    line_of_credit_data: 'owner_first_name',
    equipment_financing_data: 'owner_first_name',
    jumbo_term_loan_data: 'owner_first_name',
    transportation_factoring_data: 'first_name',
    foreclosure_bailout_data: 'borrower_first_name',
  },
  owner_last_name: {
    sba_loan_data: 'last_name',
    term_loan_10yr_data: 'last_name',
    term_loan_3_5yr_data: 'last_name',
    term_loan_data: 'last_name',
    term_loc_hybrid_data: 'last_name',
    line_of_credit_data: 'owner_last_name',
    equipment_financing_data: 'owner_last_name',
    jumbo_term_loan_data: 'owner_last_name',
    transportation_factoring_data: 'last_name',
    foreclosure_bailout_data: 'borrower_last_name',
  },
  use_of_funds: {
    term_loan_10yr_data: 'use_of_funds', term_loan_data: 'use_of_funds',
    term_loan_3_5yr_data: 'use_of_funds_description', term_loc_hybrid_data: 'use_of_proceeds',
    line_of_credit_data: 'purpose_of_credit', equipment_financing_data: 'use_of_equipment',
    real_estate_financing_data: 'detailed_use_of_funds',
  },
  annual_revenue: {
    term_loan_10yr_data: 'annual_revenue',
    term_loan_3_5yr_data: 'gross_annual_sales',
    term_loan_data: 'annual_revenue', term_loc_hybrid_data: 'annual_revenue',
    line_of_credit_data: 'annual_revenue',
    equipment_financing_data: 'annual_revenue',
  },
  monthly_revenue: {
    sba_loan_data: 'total_monthly_sales', term_loan_10yr_data: 'monthly_revenue',
    term_loan_data: 'monthly_revenue', term_loc_hybrid_data: 'monthly_revenue',
    line_of_credit_data: 'monthly_revenue',
  },
  owner_email: {
    sba_loan_data: 'email_address', term_loan_10yr_data: 'email_address',
    term_loan_3_5yr_data: 'email', term_loan_data: 'email_address',
    term_loc_hybrid_data: 'email_address',
    line_of_credit_data: 'owner_email', equipment_financing_data: 'owner_email',
    transportation_factoring_data: 'email',
    foreclosure_bailout_data: 'borrower_email',
  },
  owner_phone: {
    sba_loan_data: 'mobile_phone_number', term_loan_10yr_data: 'phone_number',
    term_loan_3_5yr_data: 'phone', term_loan_data: 'phone_number',
    term_loc_hybrid_data: 'phone_number',
    line_of_credit_data: 'owner_phone', equipment_financing_data: 'owner_phone',
    transportation_factoring_data: 'phone_number',
    foreclosure_bailout_data: 'borrower_phone',
  },
  owner_ssn: {
    sba_loan_data: 'social_security_number', term_loan_10yr_data: 'social_security_number',
    term_loan_3_5yr_data: 'primary_contact_ssn', term_loan_data: 'social_security_number',
    term_loc_hybrid_data: 'social_security_number',
    line_of_credit_data: 'owner_ssn', equipment_financing_data: 'owner_ssn',
  },
  owner_dob: {
    sba_loan_data: 'date_of_birth', term_loan_10yr_data: 'date_of_birth',
    term_loan_data: 'date_of_birth',
    term_loc_hybrid_data: 'date_of_birth',
    line_of_credit_data: 'owner_dob', equipment_financing_data: 'owner_dob',
  },
  owner_address: {
    sba_loan_data: 'home_address', term_loan_10yr_data: 'home_address',
    term_loan_3_5yr_data: 'primary_street_address', term_loan_data: 'home_address',
    term_loc_hybrid_data: 'home_address',
    line_of_credit_data: 'owner_address', equipment_financing_data: 'owner_address',
  },
  industry: {
    sba_loan_data: 'naics_code_or_nature', term_loan_10yr_data: 'industry_type',
    term_loan_3_5yr_data: 'industry', term_loan_data: 'industry_type',
    term_loc_hybrid_data: 'industry_type',
    equipment_financing_data: 'industry', line_of_credit_data: 'business_industry',
    merchant_processing_data: 'industry_type',
    jumbo_term_loan_data: 'industry_type',
  },
  doing_business_as: {
    sba_loan_data: 'doing_business_as', term_loan_10yr_data: 'doing_business_as',
    term_loan_3_5yr_data: 'company_name_dba', term_loan_data: 'doing_business_as',
    term_loc_hybrid_data: 'doing_business_as',
  },
  ownership_percentage: {
    sba_loan_data: 'ownership_percentage', term_loan_10yr_data: 'ownership_percentage',
    term_loan_3_5yr_data: 'percentage_ownership', term_loan_data: 'ownership_percentage',
    term_loc_hybrid_data: 'ownership_percentage',
    line_of_credit_data: 'owner_ownership_percentage', equipment_financing_data: 'ownership_percentage',
  },
  business_city: {
    term_loan_10yr_data: 'business_city',
    term_loan_3_5yr_data: 'physical_city',
    term_loan_data: 'business_city', term_loc_hybrid_data: 'business_city',
    equipment_financing_data: 'business_city',
  },
  business_state: {
    term_loan_10yr_data: 'business_state',
    term_loan_3_5yr_data: 'physical_state',
    term_loan_data: 'business_state', term_loc_hybrid_data: 'business_state',
    equipment_financing_data: 'business_state',
  },
  business_zip: {
    term_loan_10yr_data: 'business_zip',
    term_loan_3_5yr_data: 'physical_zip',
    term_loan_data: 'business_zip', term_loc_hybrid_data: 'business_zip',
    equipment_financing_data: 'business_zip',
  },
};

const buildReverseLookup = () => {
  const lookup = {};
  for (const [, mapping] of Object.entries(CROSS_POP_MAP)) {
    for (const [dataKey, fieldName] of Object.entries(mapping)) {
      lookup[`${dataKey}.${fieldName}`] = mapping;
    }
  }
  return lookup;
};

export const REVERSE_LOOKUP = buildReverseLookup();

export const crossPopulateFields = (fieldUpdates, selectedTypes) => {
  const result = { ...fieldUpdates };
  const activeDataKeys = selectedTypes.map(t => `${t}_data`);
  for (const [sourceDataKey, sourceFields] of Object.entries(fieldUpdates)) {
    if (!sourceFields || typeof sourceFields !== 'object') continue;
    for (const [fieldName, value] of Object.entries(sourceFields)) {
      if (value === null || value === undefined || value === '') continue;
      const mapping = REVERSE_LOOKUP[`${sourceDataKey}.${fieldName}`];
      if (!mapping) continue;
      for (const [targetDataKey, targetField] of Object.entries(mapping)) {
        if (targetDataKey === sourceDataKey || !activeDataKeys.includes(targetDataKey)) continue;
        if (!result[targetDataKey]) result[targetDataKey] = {};
        if (!result[targetDataKey][targetField]) result[targetDataKey][targetField] = value;
      }
    }
  }
  return result;
};

export const EMPTY_DEBT = {
  lender_name: '', type_of_debt: '', original_amount: '', current_balance: '',
  interest_rate: '', monthly_payment: '', open_date: '', maturity_date: '',
  secured_or_unsecured: '', current_or_delinquent: '', type_of_collateral: '',
};

export const DEBT_FIELDS = [
  { key: 'lender_name', label: 'Creditor/Lender', type: 'text' },
  { key: 'type_of_debt', label: 'Debt Type', type: 'select', options: ['Term Loan', 'Line of Credit', 'SBA Loan', 'Equipment Loan', 'Merchant Cash Advance', 'Credit Card', 'Mortgage', 'Vehicle Loan', 'Other'] },
  { key: 'original_amount', label: 'Original Amount', type: 'number' },
  { key: 'current_balance', label: 'Current Balance', type: 'number' },
  { key: 'interest_rate', label: 'Interest Rate (%)', type: 'number' },
  { key: 'monthly_payment', label: 'Monthly Payment', type: 'number' },
  { key: 'open_date', label: 'Open Date', type: 'date' },
  { key: 'maturity_date', label: 'Maturity Date', type: 'date' },
  { key: 'secured_or_unsecured', label: 'Secured/Unsecured', type: 'select', options: ['Secured', 'Unsecured'] },
  { key: 'current_or_delinquent', label: 'Status', type: 'select', options: ['Current', 'Delinquent'] },
  { key: 'type_of_collateral', label: 'Collateral Type', type: 'text' },
];
